import { transformKeys } from '@/shared/mappers/base.mapper';

import { baseApi } from '@app/baseApi';

import type { Recipe, TransformerVersion } from '@/types';
import { logger } from '@/utils/logger';

import {
  transformGetRecipeVersionsResponse,
  transformSaveRecipeRequest,
  transformValidateRecipeRequest,
} from './recipeEditor.mappers';

export interface RecipeContent {
  recipe: Recipe | null;
  configJson: string;
  config: string;
  version: number;
  languageVersion: string;
  lastModified: string;
  isDeployed: boolean;
  baseId: string;
  name?: string;
  // Additional fields needed for save
  versionId: string;
  environ?: string;
  description?: string;
  processingType?: string;
  maxDataProcTime?: string;
  templateName?: string;
}

export interface SaveRecipeRequest {
  baseId: string;
  appId: string;
  editorMode: 'visual' | 'yaml'; // Editor mode determines save behavior
  configJson?: string; // Optional - used for Visual mode
  yamlContent?: string; // Optional - used for YAML mode
  saveMode: 'sameVersion' | 'newVersion' | 'newRecipe';
  comment?: string;
  // Transformer metadata required by the API
  versionId?: string;
  name?: string;
  config?: string; // Original YAML for Visual mode saves
  environ?: string;
  version?: number;
  description?: string;
  languageVersion?: string;
  processingType?: string;
  templateName?: string;
  maxDataProcTime?: string;
}

export interface SaveRecipeResponse {
  success: boolean;
  version: number;
  lastModified: string;
  message?: string;
  // API returns updated content based on mode
  config?: string; // Updated if saved from YAML editor
  ConfigJson?: string; // Updated if saved from Visual editor
  baseId?: string;
  id?: string;
}

export const recipeEditorApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // Get recipe content (latest version)
    getRecipeContent: builder.query<RecipeContent, { baseId: string; appId: string }>({
      query: ({ baseId, appId }) => ({
        url: `transformer/versions/${baseId}?appId=${appId}`,
        endpoint: 'transformer',
      }),
      transformResponse: (response: unknown): RecipeContent => {
        const versions = transformGetRecipeVersionsResponse(response);

        if (versions.length === 0) {
          return {
            recipe: null,
            configJson: '',
            config: '',
            version: 1,
            languageVersion: '2.0',
            lastModified: '',
            isDeployed: false,
            baseId: '',
            versionId: '',
          };
        }

        const sortedVersions = [...versions].sort((a, b) => {
          const versionA = parseInt(String(a.version), 10) || 0;
          const versionB = parseInt(String(b.version), 10) || 0;
          return versionB - versionA;
        });
        const latest = sortedVersions[0];

        let recipe: Recipe | null = null;
        const configJson = latest.configJson || '';

        if (configJson) {
          try {
            const parsed = JSON.parse(configJson);
            recipe = parsed.recipe || parsed;
          } catch {
            // ConfigJson is not valid JSON, skip parsing
          }
        }

        return {
          recipe,
          configJson,
          config: latest.config ?? configJson,
          version: parseInt(String(latest.version), 10) || 1,
          languageVersion: latest.languageVersion || '2.0',
          lastModified: latest.lastModified,
          isDeployed: !!latest.deployed,
          baseId: String(latest.baseId),
          name: latest.name,
          versionId: String(latest.id),
          environ: latest.environ || '',
          description: latest.description || '',
          processingType: latest.processingType || 'batch',
          maxDataProcTime: latest.maxDataProcTime || '00:00:00',
          templateName: latest.templateName || '',
        };
      },
      // No caching - always refetch from server
      keepUnusedDataFor: 0,
      providesTags: (_result, _error, { baseId }) => [{ type: 'Recipe', id: baseId }],
    }),

    // Get all versions for a recipe
    getRecipeVersions: builder.query<TransformerVersion[], { baseId: string; appId: string }>({
      query: ({ baseId, appId }) => ({
        url: `transformer/versions/${baseId}?appId=${appId}`,
        endpoint: 'transformer',
      }),
      transformResponse: (response: unknown) => {
        const versions = transformGetRecipeVersionsResponse(response);
        return [...versions].sort((a, b) => {
          const versionA = parseInt(String(a.version), 10) || 0;
          const versionB = parseInt(String(b.version), 10) || 0;
          return versionB - versionA;
        });
      },
    }),

    // Get specific recipe version
    getRecipeVersion: builder.query<
      RecipeContent,
      { baseId: string; appId: string; version: number }
    >({
      query: ({ baseId, appId }) => ({
        url: `transformer/versions/${baseId}?appId=${appId}`,
        endpoint: 'transformer',
      }),
      transformResponse: (response: unknown, _meta, arg): RecipeContent => {
        const versions = transformGetRecipeVersionsResponse(response);
        const versionData =
          versions.find((v) => parseInt(String(v.version), 10) === arg.version) || versions[0];

        let recipe: Recipe | null = null;
        const configJson = versionData.configJson || '';

        if (configJson) {
          try {
            const parsed = JSON.parse(configJson);
            recipe = parsed.recipe || parsed;
          } catch (error) {
            logger.error('Failed to parse ConfigJson:', error);
          }
        }

        return {
          recipe,
          configJson,
          config: versionData.config ?? configJson,
          version: parseInt(String(versionData.version), 10) || 1,
          languageVersion: versionData.languageVersion || '2.0',
          lastModified: versionData.lastModified,
          isDeployed: !!versionData.deployed,
          baseId: versionData.baseId,
          name: versionData.name,
        };
      },
    }),

    // Save recipe content
    saveRecipeContent: builder.mutation<SaveRecipeResponse, SaveRecipeRequest>({
      query: ({ appId, editorMode, ...data }) => {
        const isVisualMode = editorMode === 'visual';
        return {
          url: `transformers/create/${appId}${isVisualMode ? '?configType=json' : ''}`,
          endpoint: 'transformer',
          method: 'POST',
          body: transformSaveRecipeRequest(data, editorMode),
          headers: {
            'Content-Type': 'application/json',
          },
        };
      },
      transformResponse: (response: unknown) => {
        // API returns a single transformer version object (not array)
        const version = transformKeys<TransformerVersion>(
          response,
          {
            Id: 'id',
            BaseId: 'baseId',
            Name: 'name',
            Version: 'version',
            Config: 'config',
            ConfigJson: 'configJson',
            LanguageVersion: 'languageVersion',
            LastModified: 'lastModified',
            Deployed: 'deployed',
          },
          { parseJson: false, recursive: false }
        );

        return {
          success: true,
          version: parseInt(String(version.version), 10) || 1,
          lastModified: version.lastModified,
          config: version.config, // Return for YAML mode
          ConfigJson: version.configJson, // Return for Visual mode
          baseId: version.baseId,
          id: version.id,
        };
      },
      invalidatesTags: (_result, _error, { baseId, appId }) => [
        { type: 'Recipe', id: baseId },
        { type: 'Recipe', id: `app-${appId}` },
      ],
    }),

    // Save recipe as new version
    // Matches legacy edittrans.js: callNewVersionTransformer with version=N+1, id=0
    saveAsNewVersion: builder.mutation<
      SaveRecipeResponse,
      {
        baseId: string;
        appId: string;
        configJson?: string;
        yamlContent?: string;
        editorMode: 'visual' | 'yaml';
        comment?: string;
        // All metadata required by backend (must match legacy payload)
        name?: string;
        config?: string;
        environ?: string;
        newVersion: number; // The incremented version number (versions.length + 1)
        description?: string;
        languageVersion?: string;
        processingType?: string;
        maxDataProcTime?: string;
        templateName?: string;
      }
    >({
      query: ({ appId, editorMode, ...data }) => {
        const isVisualMode = editorMode === 'visual';
        return {
          url: `transformers/create/${appId}${isVisualMode ? '?configType=json' : ''}`,
          endpoint: 'transformer',
          method: 'POST',
          body: transformSaveRecipeRequest(
            {
              baseId: data.baseId,
              configJson: data.configJson,
              yamlContent: data.yamlContent,
              config: data.config,
              saveMode: 'newVersion',
              comment: data.comment || '',
              name: data.name,
              environ: data.environ,
              version: data.newVersion,
              versionId: '0', // CRITICAL: id=0 tells backend to create new version
              description: data.description,
              languageVersion: data.languageVersion,
              processingType: data.processingType,
              maxDataProcTime: data.maxDataProcTime,
              templateName: data.templateName,
            },
            editorMode
          ),
          headers: {
            'Content-Type': 'application/json',
          },
        };
      },
      transformResponse: (response: unknown) => {
        const version = transformKeys<TransformerVersion>(
          response,
          {
            Id: 'id',
            BaseId: 'baseId',
            Name: 'name',
            Version: 'version',
            Config: 'config',
            ConfigJson: 'configJson',
            LanguageVersion: 'languageVersion',
            LastModified: 'lastModified',
            Deployed: 'deployed',
          },
          { parseJson: false, recursive: false }
        );

        return {
          success: true,
          version: parseInt(String(version.version), 10) || 1,
          lastModified: version.lastModified,
          config: version.config,
          ConfigJson: version.configJson,
          baseId: version.baseId,
          id: version.id,
        };
      },
      invalidatesTags: (_result, _error, { baseId, appId }) => [
        { type: 'Recipe', id: baseId },
        { type: 'Recipe', id: `app-${appId}` },
      ],
    }),

    // Create a brand new recipe
    // Matches legacy newtransformer.js: callCreateTransformer
    createRecipe: builder.mutation<
      SaveRecipeResponse,
      {
        appId: string;
        name: string;
        description?: string;
        environ?: string;
        config?: string;
        languageVersion?: string;
        processingType?: string;
      }
    >({
      query: ({ appId, name, description, environ, config, languageVersion, processingType }) => ({
        url: `transformers/create/${appId}`,
        endpoint: 'transformer',
        method: 'POST',
        body: {
          name: name.endsWith('.lr') ? name : `${name}.lr`,
          config: config ?? '# New Recipe\nrecipe:\n  connectors:\n  statements:\n',
          description: description || '',
          environ: environ || '',
          version: 1,
          languageVersion: languageVersion || '2.0',
          processingType: processingType || 'batch',
        },
        headers: {
          'Content-Type': 'application/json',
        },
      }),
      transformResponse: (response: unknown) => {
        const version = transformKeys<TransformerVersion>(
          response,
          {
            Id: 'id',
            BaseId: 'baseId',
            Name: 'name',
            Version: 'version',
            Config: 'config',
            ConfigJson: 'configJson',
            LanguageVersion: 'languageVersion',
            LastModified: 'lastModified',
            Deployed: 'deployed',
          },
          { parseJson: false, recursive: false }
        );

        return {
          success: true,
          version: parseInt(String(version.version), 10) || 1,
          lastModified: version.lastModified,
          config: version.config,
          ConfigJson: version.configJson,
          baseId: version.baseId,
          id: version.id,
        };
      },
      invalidatesTags: (_result, _error, { appId }) => [
        { type: 'Recipe', id: `app-${appId}` },
        'Recipe',
      ],
    }),

    // Validate recipe
    validateRecipe: builder.mutation<{ valid: boolean; errors: string[] }, { configJson: string }>({
      query: ({ configJson }) => ({
        url: 'transformers/validate',
        endpoint: 'transformer',
        method: 'POST',
        body: transformValidateRecipeRequest({ configJson }),
      }),
    }),
  }),
});

export const {
  useGetRecipeContentQuery,
  useGetRecipeVersionsQuery,
  useGetRecipeVersionQuery,
  useSaveRecipeContentMutation,
  useSaveAsNewVersionMutation,
  useCreateRecipeMutation,
  useValidateRecipeMutation,
} = recipeEditorApi;
